import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Key, Loader2, Plus, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import { apiKeysApi, ApiKeyStatus, CreatedHubApiKey, HubApiKey } from '@/lib/api';
import { CreateApiKeyModal } from '@/components/modals/CreateApiKeyModal';
import { RevokeApiKeyModal } from '@/components/modals/RevokeApiKeyModal';

const STATUS_STYLES: Record<ApiKeyStatus, string> = {
  active: 'bg-success/10 text-success',
  expired: 'bg-warning/10 text-warning',
  revoked: 'bg-grey-100 text-grey-500',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** "3 days ago" is the question being asked of lastUsedAt, not the exact stamp. */
function formatRelative(value: string | null): string {
  if (!value) return 'Never used';
  const then = new Date(value).getTime();
  const minutes = Math.round((Date.now() - then) / 60_000);
  if (minutes < 2) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(value);
}

/**
 * The reveal panel. This is the ONE moment the raw key exists outside the
 * consumer's config, so it stays on screen until it is dismissed — never a
 * toast — and says plainly that it will not be shown again.
 */
function RevealedKey({ created, onDismiss }: { created: CreatedHubApiKey; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard can be blocked; the key is selectable on screen either way.
    }
  };

  return (
    <Card className="border-accent bg-accent/5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="text-heading-3 text-secondary">Copy your key now</h3>
          <p className="text-sm text-grey-600 mt-1">
            <strong className="text-secondary">You will not see this again.</strong> The Hub stores
            only a hash of it — if you lose it, revoke this key and create another.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Done
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 px-3 py-2.5 rounded-lg bg-white border border-grey-100 font-mono text-sm text-secondary break-all">
          {created.key}
        </code>
        <Button onClick={copy} className="shrink-0">
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      <p className="text-xs text-grey-500 mt-3">
        Send it as the <code className="font-mono">X-API-Key</code> header on{' '}
        <code className="font-mono">/api/v1/internal/*</code>. Verify it with{' '}
        <code className="font-mono">GET /api/v1/internal/health</code>, which reports the name and
        scopes it authenticated as.
      </p>
    </Card>
  );
}

function ScopeChips({ scopes }: { scopes: string[] }) {
  if (scopes.length === 0) {
    return <span className="text-xs text-grey-400">No scopes</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {scopes.map((scope) => (
        <span
          key={scope}
          className={cn(
            'px-2 py-0.5 rounded text-xs font-mono',
            scope === 'admin' ? 'bg-warning/10 text-warning' : 'bg-grey-100 text-grey-600',
          )}
        >
          {scope}
        </span>
      ))}
    </div>
  );
}

function ApiKeyRow({ apiKey, onRevoke }: { apiKey: HubApiKey; onRevoke: (key: HubApiKey) => void }) {
  const inactive = apiKey.status !== 'active';

  return (
    <div
      className={cn(
        'py-4 px-4 bg-white rounded-lg border border-grey-100',
        inactive && 'opacity-60',
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-secondary truncate">{apiKey.name}</p>
            <span
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded-full capitalize',
                STATUS_STYLES[apiKey.status],
              )}
            >
              {apiKey.status}
            </span>
          </div>

          <code className="block text-xs text-grey-500 font-mono mt-1">{apiKey.prefix}…</code>

          <div className="mt-2">
            <ScopeChips scopes={apiKey.scopes} />
          </div>

          <p className="text-xs text-grey-500 mt-2">
            {formatRelative(apiKey.lastUsedAt)}
            {' · '}
            {apiKey.expiresAt
              ? `Expires ${formatDate(apiKey.expiresAt)}`
              : 'No expiry'}
            {' · '}
            Created {formatDate(apiKey.createdAt)}
            {apiKey.createdByName ? ` by ${apiKey.createdByName}` : ''}
          </p>
        </div>

        {apiKey.status === 'active' && (
          <button
            onClick={() => onRevoke(apiKey)}
            className="self-start p-2 text-grey-600 hover:text-error hover:bg-error/10 rounded transition-colors"
            title="Revoke key"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export function SettingsApiKeys() {
  const [showCreate, setShowCreate] = useState(false);
  const [revoking, setRevoking] = useState<HubApiKey | null>(null);
  const [created, setCreated] = useState<CreatedHubApiKey | null>(null);

  const { data: keys, isLoading, isError, error } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => apiKeysApi.list(),
  });

  return (
    <div className="space-y-6">
      {created && <RevealedKey created={created} onDismiss={() => setCreated(null)} />}

      <Card>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-heading-3 text-secondary">API Keys</h2>
            <p className="text-sm text-grey-500 mt-1">
              Keys that let another app read and write Hub data through{' '}
              <code className="font-mono text-xs">/api/v1/internal/*</code> — no user account, no
              password. Each key carries only the permissions you grant it.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="shrink-0">
            <Plus className="w-4 h-4 mr-2" />
            New key
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
          </div>
        ) : isError ? (
          <div className="p-4 rounded-lg bg-error/10 text-error text-sm">
            Could not load API keys — {(error as any)?.message || 'unknown error'}
          </div>
        ) : keys && keys.length > 0 ? (
          <div className="space-y-2">
            {keys.map((apiKey) => (
              <ApiKeyRow key={apiKey.id} apiKey={apiKey} onRevoke={setRevoking} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Key}
            title="No API keys yet"
            description="Create one to connect another Solid app — financials, for instance — to the Hub's project data."
            action={
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New key
              </Button>
            }
          />
        )}
      </Card>

      <Card>
        <h3 className="text-heading-3 text-secondary mb-2">How a key is used</h3>
        <div className="space-y-2 text-sm text-grey-600">
          <p>
            The consumer sends the key as an <code className="font-mono">X-API-Key</code> header.
            <code className="font-mono"> GET /api/v1/internal/health</code> answers with the key&apos;s
            name, its scopes, and whether it was resolved from this screen or from a legacy
            environment variable — so there is never a doubt about which key is talking.
          </p>
          <p>
            A key missing a route&apos;s permission gets a <strong>403</strong>, not a 401: it proved
            who it is, it simply may not do that. Revoking is immediate.
          </p>
          <p className="text-grey-500">
            Keys still set in the server environment keep working as a bootstrap, and behave as
            though they hold every permission — they predate this screen and cannot be revoked from
            it. Move a consumer onto a key issued here to get real permissions and one-click
            revocation.
          </p>
        </div>
      </Card>

      {showCreate && (
        <CreateApiKeyModal
          onClose={() => setShowCreate(false)}
          onCreated={(key) => {
            setCreated(key);
            setShowCreate(false);
          }}
        />
      )}

      {revoking && <RevokeApiKeyModal apiKey={revoking} onClose={() => setRevoking(null)} />}
    </div>
  );
}
